<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import InfoSheetButton from '@uiKit/components/InfoSheetButton.vue'
import type { InfoSheetContent } from '@uiKit/components/InfoSheet.vue'

// White key/value card used on every sell step (Figma "Card", 34842:194759):
// optional grey eyebrow, ruled rows, an optional emphasised total row.

export type SellRowTone = 'default' | 'success' | 'warning' | 'danger'

export interface SellSummaryRow {
  label: string
  value: string
  tone?: SellRowTone
  /** ⓘ next to the label, opening this sheet. */
  info?: InfoSheetContent
  /** Last-row total — bold label, larger value. */
  isTotal?: boolean
}

interface Props {
  rows: SellSummaryRow[]
  /** Grey uppercase heading, e.g. "LISTING SUMMARY". */
  eyebrow?: string
  /** `sm` labels (12px) on the dense review summary; `md` (13px) elsewhere. */
  labelSize?: 'sm' | 'md'
}

withDefaults(defineProps<Props>(), { labelSize: 'md' })

const TONE_COLOR: Record<SellRowTone, string> = {
  default: 'default',
  success: 'var(--p-success-600)',
  warning: 'var(--p-warning-600)',
  danger: 'var(--p-danger-600)',
}
</script>

<template>
  <Box direction="col" gap="2px" class="sell-summary">
    <Text
      v-if="eyebrow"
      is="h3"
      type="caption"
      weight="bold"
      color="var(--p-sliceal-gray-400)"
      class="sell-summary__eyebrow"
      >{{ eyebrow }}</Text
    >
    <Box
      v-for="row in rows"
      :key="row.label"
      align="center"
      justify="between"
      gap="3"
      class="sell-summary__row"
    >
      <Box align="center" gap="6px" class="sell-summary__label">
        <Text
          is="span"
          :type="row.isTotal ? 'body' : labelSize === 'sm' ? 'caption' : 'body-sm'"
          :weight="row.isTotal ? 'semibold' : 'regular'"
          :color="row.isTotal ? 'default' : 'var(--p-surface-500)'"
          class="sell-summary__text"
          >{{ row.label }}</Text
        >
        <InfoSheetButton
          v-if="row.info"
          :content="row.info"
          :label="`About ${row.label}`"
          :size="14"
        />
      </Box>
      <Text
        is="span"
        :type="row.isTotal ? 'heading' : 'body'"
        :weight="row.isTotal ? 'semibold' : 'medium'"
        :color="TONE_COLOR[row.tone ?? 'default']"
        align="right"
        class="sell-summary__text sell-summary__value"
        ><CurrencyText :text="row.value"
      /></Text>
    </Box>
  </Box>
</template>

<style scoped>
.sell-summary {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid var(--p-sliceal-gray-200);
  border-radius: 16px;
  background: var(--p-surface-0);
  box-sizing: border-box;
}

.sell-summary__eyebrow {
  margin: 0;
  line-height: 1.4;
}

.sell-summary__row {
  padding: 10px 0;
  border-bottom: 1px solid var(--p-sliceal-gray-200);
}

.sell-summary__row:last-child {
  border-bottom: none;
}

.sell-summary__label {
  flex-shrink: 0;
}

.sell-summary__text {
  line-height: 1.3;
}

/* Long property names wrap under themselves rather than push the label. */
.sell-summary__value {
  min-width: 0;
  overflow-wrap: anywhere;
}
</style>
